import { Filters } from '@/core/collections/filters/filter.interface';
import { http } from '@/core/helpers/http.helper';
import { ApiResource } from '@/core/models/api-resource.interface';
import { LocationQuery } from 'vue-router';
import { FilterInfoType } from '../filter-info/filter-info.interface';
import { Position } from './position.interface';

class POSITION_INCLUDES_CLASS {
   all: Record<'includes' | 'appends', Array<string>> = {
      includes: [
         'city',
         'city.country',
         'city.province',
         'customerAddress',
         'contact',
         'contact.communication_language',
         'contact.salutation',
         'customer',
         'customer.company',
         'customer.contacts',
         'customer.contacts.communication_language',
         'customer.contacts.salutation',
         'descriptions',
         'employer',
         'external_sources',
         'function_level',
         'knockout_questions',
         'profile',
         'publicationSites',
         'publications',
         'publications.external_source',
         'publications.publication_statuses',
         'status',
         'statute',
         'team',
         'user_spoc',
         'wage_type',
      ],
      appends: [],
   };
   list: Record<'includes' | 'appends', Array<string>> = {
      includes: ['customer', 'status', 'team', 'customer.company', 'customerAddress', 'city', 'city.country', 'city.province'],
      appends: [],
   };
   dropdown: Record<'includes' | 'appends', Array<string>> = {
      includes: ['customer', 'status', 'team', 'customer.company', 'customerAddress', 'city', 'city.country', 'city.province'],
      appends: [],
   };

   constructor() {
      this.check(this.dropdown, 'dropdown');
   }

   check(includes: Record<'includes' | 'appends', Array<string>>, name: string): boolean {
      const includesDiff = includes.includes.filter((i) => !this.all.includes.includes(i));
      const appendsDiff = includes.appends.filter((i) => !this.all.appends.includes(i));

      if (includesDiff.length || appendsDiff.length) {
         console.group(`CANDIDATE_INCLUDES_CLASS: ${name}`);
         console.info('Missing includes:', includesDiff);
         console.info('Missing appends:', appendsDiff);
         console.groupEnd();
      }

      return !includesDiff.length && !appendsDiff.length;
   }
}

export const POSITION_INCLUDES = new POSITION_INCLUDES_CLASS();

export const POSITION_DROPDOWN_INCLUDES = [
   'customer',
   'status',
   'team',
   'customer.company',
   'customerAddress',
   'city',
   'city.country',
   'city.province',
];

export type PositionTabTypes = 'profile' | 'matches' | 'publications';

export class PositionService {
   async getFilteredPositions(query: LocationQuery, filters: Filters, targetType: FilterInfoType) {
      let url = '';
      if (targetType === 'position') url = '/positions';
      if (targetType === 'advertsdata-position') url = '/advertsdata-positions';
      if (targetType === 'online-position') url = '/online-positions';
      const response = await http.post<ApiResource<Array<Position>>>(`${url}/search`, filters, {
         params: { ...query },
      });
      return response.data;
   }
   async getPositions(query: LocationQuery) {
      const response = await http.get<ApiResource<Array<Position>>>(`/positions`, { params: { ...query } });
      return response.data;
   }
   async autocomplete(search: string) {
      const response = await http.get<ApiResource<Array<string>>>(`/positions/autocomplete`, {
         params: { search, per_page: 25 },
      });
      return response.data;
   }
   async getPosition(positionId: number, query?: LocationQuery) {
      const response = await http.get<ApiResource<Position>>(`/positions/${positionId}`, { params: { ...query } });
      return response.data.data;
   }
   async postPosition(position: Position) {
      const response = await http.post<ApiResource<Position>>(`/positions`, position);
      return response.data;
   }
   async putPosition(position: Position) {
      const response = await http.put<ApiResource<Position>>(`/positions/${position.id}`, position, {
         params: {
            withTranslations: true,
         },
      });
      return response.data?.data;
   }
   async deletePositionsBulk(positionIds: Array<number>) {
      const response = await http.delete(`/positions`, { data: { ids: positionIds } });
      return response.data;
   }
   async generateXml(positionId: number) {
      const response = await http.get(`/positions/${positionId}/xml`);
      return response.data;
   }
   async deletePosition(positionId: number, force: boolean) {
      const response = await http.delete(`/positions/${positionId}`, { params: { force } });
      return response.data;
   }
   async checkDeletePosition(positionId: number, force: boolean) {
      const response = await http.get(`/positions/${positionId}/check-delete`, { params: { force } });
      return response.data;
   }
   async bulkTakePositionOffline(positions: Position[]) {
      const body = {
         position_ids: positions.map((position) => position.id),
      };

      const response = await http.put<ApiResource<Position[]>>(`/positions-bulk-set-offline`, body);
      return response.data;
   }
   async bulkUpdatePositionStatus(positions: Position[], statusId: number) {
      const body = {
         position_ids: positions.map((position) => position.id),
         status_id: statusId,
      };

      const response = await http.put<ApiResource<Position[]>>(`/positions-bulk-update-status`, body);
      return response.data;
   }
}
